import {
  AddRounded,
  ArrowBack,
  BusinessOutlined,
  DescriptionOutlined,
  InfoOutlined,
  LocationOnOutlined,
  PaymentsOutlined,
  SaveOutlined,
  SchoolOutlined,
  WorkOutline,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  SvgIconProps,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { ComponentType, ReactNode, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import locales from "../../../constants/locales";
import { DEFAULT_ORG_ID } from "../../../constants";
import Loading from "../../../components/loading/loading";
import HTMLEditor from "../../../components/html_editor/html_editor";
import OrgSelector from "../../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import MainLayout from "../../../layouts/main/main_layout";
import useUser from "../../../providers/user/user_provider";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import { black, greyEnabled, orange, orangeLightActive, purple } from "../../../theme/colors";
import {
  createJob,
  getFilters,
  getJobDetails,
  updateJob,
} from "../../../services/jobs/jobs";
import {
  getCityMasterData,
  getDistrictMasterData,
  getStateMasterData,
} from "../../../services/master/master_service";
import { getSchemeMaster } from "../../../services/schemes/schemes";
import { JobFormValues, MODE_OF_CONTACT } from "../model/job_form_model";

const defaultValues: JobFormValues = {
  jobPosition: "",
  jobTypeId: null,
  categoryId: null,
  subcategoryId: null,
  stateId: null,
  cityId: null,
  districtId: null,
  educationId: [],
  occupationId: [],
  workExperienceId: null,
  workModeId: null,
  company: "",
  modeOfContact: null,
  contactNo: "",
  contactEmail: "",
  jobUrl: "",
  salaryRangeId: null,
  description: "",
  companyDescription: "",
  rolesAndResponsibilities: "",
  expiryDate: null,
  noOfPosts: 1,
};

const MODE_OF_CONTACT_OPTIONS = [
  { id: MODE_OF_CONTACT.URL, value: "Application URL" },
  { id: MODE_OF_CONTACT.PHONE, value: "Phone Number" },
  { id: MODE_OF_CONTACT.EMAIL, value: "Email Address" },
];

// A small bold, uppercase divider label — breaks the form into scannable
// groups instead of one long flat list of fields (same convention as CMS's
// add_edit_news.tsx). The icon gives each section a quick visual anchor
// when scanning a long form.
function SectionHeader({
  children,
  mt = 4,
  icon: Icon,
}: {
  children: ReactNode;
  mt?: number;
  icon: ComponentType<SvgIconProps>;
}) {
  return (
    <Box mt={mt} mb={1.5}>
      <Box display="flex" alignItems="center" gap={0.75}>
        <Icon sx={{ fontSize: 18, color: purple }} />
        <Typography variant="overline" fontWeight={700} color={purple} sx={{ letterSpacing: 0.6 }}>
          {children}
        </Typography>
      </Box>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

export default function AddEditJob({ viewMode = false }: { viewMode?: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useFeedback();
  const { userPermissions } = useUser();
  const isEdit = !!id;
  // A job is always created in English; other languages are added
  // afterwards from the Edit screen, same convention as CMS News/Schemes.
  const [selectedLocale, setSelectedLocale] = useState("en");
  const { control, handleSubmit, watch, setValue, reset } = useForm<JobFormValues>({
    defaultValues,
  });
  const { selectedOrgId } = useSelectedOrgStore();

  const { data: jobDetails, isLoading: isJobLoading } = useQuery(
    ["job-form-details", id, selectedLocale],
    async () => {
      const response = await getJobDetails({ jobId: +id!, locale: selectedLocale });
      return response?.data?.data;
    },
    { enabled: !!id }
  );

  const { data: filterList } = useQuery(["jobFilters"], async () => {
    const response = await getFilters();
    return response?.data?.data;
  });

  // Education/Occupation master lists aren't part of Jobs' own filters
  // endpoint - they come from the same cross-service master bundle Schemes
  // and Users already use (EducationMaster/OccupationMaster are shared
  // tables, not Jobs-specific).
  const { data: schemeMaster } = useQuery(["jobFormSchemeMaster"], async () => {
    const response = await getSchemeMaster();
    return response?.data?.data;
  });

  useEffect(() => {
    if (jobDetails) {
      reset({
        jobPosition: jobDetails.jobPosition || "",
        jobTypeId: jobDetails.jobTypeId ?? null,
        categoryId: jobDetails.categoryId ?? null,
        subcategoryId: jobDetails.subcategoryId ?? null,
        stateId: jobDetails.stateId ?? null,
        // A saved job's city/district is only ever null because "All" was
        // explicitly chosen at save time (both are required choices on
        // this form) - map that back to the "All" sentinel (0), distinct
        // from a brand-new form's "nothing chosen yet" (null).
        cityId: jobDetails.cityId ?? 0,
        districtId: jobDetails.districtId ?? 0,
        educationId: jobDetails.educationIds || [],
        occupationId: jobDetails.occupationIds || [],
        workExperienceId: jobDetails.workExperienceId ?? null,
        workModeId: jobDetails.workModeId ?? null,
        company: jobDetails.company || "",
        modeOfContact: jobDetails.modeOfContact ?? null,
        contactNo: jobDetails.jobContactNo || "",
        contactEmail: jobDetails.jobEmailId || "",
        jobUrl: jobDetails.jobUrl || "",
        salaryRangeId: jobDetails.salaryRangeId ?? null,
        description: jobDetails.description || "",
        companyDescription: jobDetails.companyDescription || "",
        rolesAndResponsibilities: jobDetails.rolesAndResponsibilities || "",
        expiryDate: jobDetails.expiryDate || null,
        noOfPosts: jobDetails.noOfPosts ?? 1,
      });
    }
  }, [jobDetails, reset]);

  const stateId = watch("stateId");
  const categoryId = watch("categoryId");
  const modeOfContact = watch("modeOfContact");
  const jobPosition = watch("jobPosition");
  const company = watch("company");

  // The State master list has a real "All" row (value "All", same
  // convention used app-wide, e.g. Users' gender/marital-status master
  // lists) - a government job can be applicable nationwide. District/City
  // have no such row, so "All districts"/"All cities" within a state is
  // represented client-side as a sentinel id (0, never a real master id)
  // and translated to a literal null on submit, matching jobs.jobs'
  // already-nullable city_id/district_id columns.
  const { data: stateList } = useQuery(["jobFormStateList"], async () => {
    const response = await getStateMasterData();
    return response?.data?.data?.list || [];
  });
  const selectedStateIsAll =
    stateList?.find((option: any) => option.id === stateId)?.value === "All";
  const ALL_OPTION = { id: 0, value: "All" };

  const districtId = watch("districtId");
  const districtIsAll = districtId === 0;

  const { data: cityList } = useQuery(["jobFormCityList", stateId, districtIsAll], async () => {
    if (!stateId) return [];
    // State "All" or District "All" both mean "every city" too - bind the
    // City field to the same "All" option rather than fetching a real list
    // it can't meaningfully narrow anyway.
    if (selectedStateIsAll || districtIsAll) return [ALL_OPTION];
    const response = await getCityMasterData(stateId);
    return [ALL_OPTION, ...(response?.data?.data?.list || [])];
  });
  const { data: districtList } = useQuery(["jobFormDistrictList", stateId], async () => {
    if (!stateId) return [];
    if (selectedStateIsAll) return [ALL_OPTION];
    const response = await getDistrictMasterData(stateId);
    return [ALL_OPTION, ...(response?.data?.data?.list || [])];
  });

  const selectedCategory = useMemo(
    () => filterList?.categories?.find((category: any) => category?.id === categoryId),
    [filterList?.categories, categoryId]
  );
  const subcategoryOptions = selectedCategory?.subcategories || [];
  // Sub Category only applies to Government Jobs - getFilters() always
  // fetches with a fixed `locale: "en"` header, so this name comparison is
  // reliable regardless of the admin's own UI language.
  const isGovernmentCategory = selectedCategory?.value === "Government Jobs";

  // Changing State clears City/District (they belong to the previous
  // state) - bound+disabled to "All" when the new state is itself "All",
  // otherwise reset to unselected; changing Category clears Sub Category
  // the same way.
  const previousStateId = watch("stateId");
  useEffect(() => {
    setValue("cityId", selectedStateIsAll ? 0 : null);
    setValue("districtId", selectedStateIsAll ? 0 : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousStateId, selectedStateIsAll]);
  // District "All" implies every city in the state too - bind+disable City
  // to "All" the same way State "All" does.
  useEffect(() => {
    if (districtIsAll) setValue("cityId", 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtIsAll]);
  useEffect(() => {
    setValue("subcategoryId", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = (values: JobFormValues) => {
    setIsSubmitting(true);
    const targetOrgId = selectedOrgId ?? userPermissions?.orgId ?? DEFAULT_ORG_ID;
    const payload = {
      jobPosition: values.jobPosition,
      jobTypeId: values.jobTypeId ?? undefined,
      categoryId: values.categoryId,
      subcategoryId: values.subcategoryId,
      stateId: values.stateId,
      // The "All" sentinel (0) only exists in this form's own state - the
      // backend's nullable city_id/district_id columns are what actually
      // represent "applicable to all districts/cities".
      cityId: values.cityId ? values.cityId : null,
      districtId: values.districtId ? values.districtId : null,
      educationId: values.educationId,
      occupationId: values.occupationId,
      workExperienceId: values.workExperienceId,
      workModeId: values.workModeId ?? undefined,
      company: values.company,
      modeOfContact: values.modeOfContact,
      contactNo: values.modeOfContact === MODE_OF_CONTACT.PHONE ? values.contactNo : undefined,
      contactEmail:
        values.modeOfContact === MODE_OF_CONTACT.EMAIL ? values.contactEmail : undefined,
      jobUrl: values.modeOfContact === MODE_OF_CONTACT.URL ? values.jobUrl : undefined,
      salaryRangeId: values.salaryRangeId ?? undefined,
      description: values.description || undefined,
      companyDescription: values.companyDescription || undefined,
      rolesAndResponsibilities: values.rolesAndResponsibilities || undefined,
      expiryDate: values.expiryDate || undefined,
      noOfPosts: values.noOfPosts || undefined,
      ...(isEdit ? {} : { orgId: targetOrgId }),
    };

    const request = isEdit
      ? updateJob(+id!, selectedLocale, payload)
      : createJob(payload);

    request
      .then(() => {
        showSnackbar(isEdit ? "Job updated successfully" : "Job added successfully");
        queryClient.invalidateQueries({ queryKey: ["JobList"] });
        // The list query above doesn't cover this item's own details cache
        // (keyed separately by id+locale) - without this, re-opening Edit
        // within the default staleTime would serve the stale pre-update data.
        queryClient.invalidateQueries({ queryKey: ["job-form-details", id] });
        queryClient.invalidateQueries({ queryKey: ["job-application-details", id] });
        navigate("/jobs");
      })
      .catch((e) => {
        if (e?.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const requiredFieldsFilled =
    !!jobPosition?.trim() &&
    !!company?.trim() &&
    !!watch("categoryId") &&
    (!isGovernmentCategory || !!watch("subcategoryId")) &&
    !!watch("stateId") &&
    (selectedStateIsAll || watch("cityId") !== null) &&
    (selectedStateIsAll || watch("districtId") !== null) &&
    (watch("educationId")?.length || 0) > 0 &&
    (watch("occupationId")?.length || 0) > 0 &&
    !!watch("workExperienceId") &&
    !!watch("modeOfContact") &&
    (modeOfContact !== MODE_OF_CONTACT.URL || !!watch("jobUrl")?.trim()) &&
    (modeOfContact !== MODE_OF_CONTACT.PHONE || !!watch("contactNo")?.trim()) &&
    (modeOfContact !== MODE_OF_CONTACT.EMAIL || !!watch("contactEmail")?.trim());

  return (
    <MainLayout>
      <Card sx={{ width: "100%", borderRadius: 4, m: 1 }} elevation={1}>
        <CardContent>
          <Box display="flex" flexDirection="row" alignItems="center">
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" fontWeight="600" ml={1}>
              {viewMode ? "View Job" : isEdit ? "Edit Job" : "Add Job"}
            </Typography>
          </Box>
          <Divider sx={{ my: 3, opacity: 0.5, borderBottom: "1px dotted #7C797C" }} />

          {isEdit && isJobLoading ? (
            <Loading label="Loading job..." />
          ) : isEdit && !viewMode && jobDetails?.source === "App" ? (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: orangeLightActive, border: `1px solid ${orangeLightActive}` }}
            >
              <InfoOutlined sx={{ color: orange, fontSize: 20 }} />
              <Typography variant="body2" color={black}>
                This job was submitted by an app user and can't be edited from the admin portal — use Approve/Reject
                from the Job Details screen instead.
              </Typography>
            </Box>
          ) : (
            <>
              {!isEdit && !viewMode && (
                <Box maxWidth={360}>
                  <OrgSelector label="Organisation" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
                </Box>
              )}

              {isEdit && (
                <>
                  <Tabs
                    value={locales.findIndex((locale) => locale.code === selectedLocale)}
                    onChange={(_, index: number) => setSelectedLocale(locales[index].code)}
                    variant="scrollable"
                    scrollButtons="auto"
                    TabIndicatorProps={{ style: { backgroundColor: purple, height: 3, borderRadius: 3 } }}
                    sx={{ mt: 2 }}
                  >
                    {locales.map((locale) => (
                      <Tab
                        key={locale.code}
                        label={locale.name}
                        sx={{ textTransform: "capitalize", minWidth: 90, color: greyEnabled, "&.Mui-selected": { color: purple } }}
                      />
                    ))}
                  </Tabs>
                  <Divider />

                  {jobDetails?.isTranslated === false && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: orangeLightActive, border: `1px solid ${orangeLightActive}` }}
                    >
                      <InfoOutlined sx={{ color: orange, fontSize: 20 }} />
                      <Typography variant="body2" color={black}>
                        This job has no content in{" "}
                        {locales.find((locale) => locale.code === selectedLocale)?.name || selectedLocale} yet — fill
                        in the details below to add this translation.
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              <SectionHeader icon={WorkOutline} mt={!isEdit && !viewMode ? 3 : 2}>Job Information</SectionHeader>

              <Controller
                name="jobPosition"
                control={control}
                rules={{ required: "Job title is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label="Job Title"
                    margin="normal"
                    disabled={viewMode}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Box display="flex" flexDirection="row" gap={4}>
                <Controller
                  name="jobTypeId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      fullWidth
                      disabled={viewMode}
                      options={filterList?.jobType || []}
                      getOptionLabel={(option: any) => option?.value || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value}
                      value={filterList?.jobType?.find((option: any) => option.id === field.value) || null}
                      onChange={(_, newValue: any) => field.onChange(newValue?.id ?? null)}
                      renderInput={(params) => <TextField {...params} label="Job Type" margin="normal" />}
                    />
                  )}
                />
                <Controller
                  name="workModeId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      fullWidth
                      disabled={viewMode}
                      options={filterList?.workMode || []}
                      getOptionLabel={(option: any) => option?.value || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value}
                      value={filterList?.workMode?.find((option: any) => option.id === field.value) || null}
                      onChange={(_, newValue: any) => field.onChange(newValue?.id ?? null)}
                      renderInput={(params) => <TextField {...params} label="Employment Type" margin="normal" />}
                    />
                  )}
                />
              </Box>

              <Box display="flex" flexDirection="row" gap={4}>
                <Controller
                  name="categoryId"
                  control={control}
                  rules={{ required: "Category is required" }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      fullWidth
                      disabled={viewMode}
                      options={filterList?.categories || []}
                      getOptionLabel={(option: any) => option?.value || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value}
                      value={filterList?.categories?.find((option: any) => option.id === field.value) || null}
                      onChange={(_, newValue: any) => field.onChange(newValue?.id ?? null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Category"
                          margin="normal"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
                {isGovernmentCategory && (
                  <Controller
                    name="subcategoryId"
                    control={control}
                    rules={{ required: "Sub category is required" }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        fullWidth
                        disabled={viewMode || !categoryId}
                        options={subcategoryOptions}
                        getOptionLabel={(option: any) => option?.value || ""}
                        isOptionEqualToValue={(option: any, value: any) => option.id === value}
                        value={subcategoryOptions.find((option: any) => option.id === field.value) || null}
                        onChange={(_, newValue: any) => field.onChange(newValue?.id ?? null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            label="Sub Category"
                            margin="normal"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message || (!categoryId ? "Select a category first" : "")}
                          />
                        )}
                      />
                    )}
                  />
                )}
              </Box>

              <SectionHeader icon={BusinessOutlined}>Company &amp; Contact</SectionHeader>

              <Controller
                name="company"
                control={control}
                rules={{ required: "Company name is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label="Company Name"
                    margin="normal"
                    disabled={viewMode}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="modeOfContact"
                control={control}
                rules={{ required: "Mode of contact is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    required
                    label="Mode of Contact"
                    margin="normal"
                    disabled={viewMode}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    value={field.value ?? ""}
                  >
                    {MODE_OF_CONTACT_OPTIONS.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              {modeOfContact === MODE_OF_CONTACT.URL && (
                <Controller
                  name="jobUrl"
                  control={control}
                  rules={{ required: "Application URL is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Application URL"
                      margin="normal"
                      disabled={viewMode}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
              {modeOfContact === MODE_OF_CONTACT.PHONE && (
                <Controller
                  name="contactNo"
                  control={control}
                  rules={{
                    required: "Contact number is required",
                    pattern: { value: /^[0-9]{10}$/, message: "Enter a valid 10-digit mobile number" },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Contact Number"
                      margin="normal"
                      disabled={viewMode}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                    />
                  )}
                />
              )}
              {modeOfContact === MODE_OF_CONTACT.EMAIL && (
                <Controller
                  name="contactEmail"
                  control={control}
                  rules={{
                    required: "Contact email is required",
                    pattern: { value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: "Enter a valid email address" },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Contact Email"
                      margin="normal"
                      disabled={viewMode}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}

              <SectionHeader icon={DescriptionOutlined}>Description &amp; Responsibilities</SectionHeader>

              <Typography variant="body2" color={greyEnabled} mb={1}>
                Job Summary &amp; Description
              </Typography>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <HTMLEditor value={field.value} onChange={field.onChange} placeholder="Enter job description" readOnly={viewMode} />
                )}
              />

              <Typography variant="body2" color={greyEnabled} mt={3} mb={1}>
                About the Company
              </Typography>
              <Controller
                name="companyDescription"
                control={control}
                render={({ field }) => (
                  <HTMLEditor value={field.value} onChange={field.onChange} placeholder="Enter company description" readOnly={viewMode} />
                )}
              />

              <Typography variant="body2" color={greyEnabled} mt={3} mb={1}>
                Roles &amp; Responsibilities
              </Typography>
              <Controller
                name="rolesAndResponsibilities"
                control={control}
                render={({ field }) => (
                  <HTMLEditor value={field.value} onChange={field.onChange} placeholder="Enter roles and responsibilities" readOnly={viewMode} />
                )}
              />

              <SectionHeader icon={LocationOnOutlined}>Location</SectionHeader>

              <Box display="flex" flexDirection="row" gap={4}>
                <Controller
                  name="stateId"
                  control={control}
                  rules={{ required: "State is required" }}
                  render={({ field, fieldState }) => (
                    <StateAutocomplete
                      value={field.value}
                      onChange={field.onChange}
                      disabled={viewMode}
                      error={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="districtId"
                  control={control}
                  rules={{
                    validate: (value) =>
                      selectedStateIsAll || value !== null || "District is required (choose \"All\" if applicable to every district)",
                  }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      fullWidth
                      disabled={viewMode || !stateId || selectedStateIsAll}
                      options={districtList || []}
                      getOptionLabel={(option: any) => option?.value || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value}
                      value={(districtList || []).find((option: any) => option.id === field.value) || null}
                      onChange={(_, newValue: any) => field.onChange(newValue?.id ?? null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="District"
                          margin="normal"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name="cityId"
                  control={control}
                  rules={{
                    validate: (value) =>
                      selectedStateIsAll || value !== null || "City is required (choose \"All\" if applicable to every city)",
                  }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      fullWidth
                      disabled={viewMode || !stateId || selectedStateIsAll || districtIsAll}
                      options={cityList || []}
                      getOptionLabel={(option: any) => option?.value || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value}
                      value={(cityList || []).find((option: any) => option.id === field.value) || null}
                      onChange={(_, newValue: any) => field.onChange(newValue?.id ?? null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="City"
                          margin="normal"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Box>

              <SectionHeader icon={SchoolOutlined}>Eligibility</SectionHeader>

              <Box display="flex" flexDirection="row" gap={4}>
                <Controller
                  name="educationId"
                  control={control}
                  rules={{ validate: (value) => value.length > 0 || "Select at least one qualification" }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      multiple
                      fullWidth
                      disabled={viewMode}
                      options={schemeMaster?.education || []}
                      getOptionLabel={(option: any) => option?.value || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      value={(schemeMaster?.education || []).filter((option: any) => field.value.includes(option.id))}
                      onChange={(_, newValue: any[]) => field.onChange(newValue.map((option) => option.id))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Educational Qualification"
                          margin="normal"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name="occupationId"
                  control={control}
                  rules={{ validate: (value) => value.length > 0 || "Select at least one occupation" }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      multiple
                      fullWidth
                      disabled={viewMode}
                      options={schemeMaster?.occupation || []}
                      getOptionLabel={(option: any) => option?.value || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      value={(schemeMaster?.occupation || []).filter((option: any) => field.value.includes(option.id))}
                      onChange={(_, newValue: any[]) => field.onChange(newValue.map((option) => option.id))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Occupation"
                          margin="normal"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Box>

              <Controller
                name="workExperienceId"
                control={control}
                rules={{ required: "Experience required is mandatory" }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    fullWidth
                    disabled={viewMode}
                    options={filterList?.workExperience || []}
                    getOptionLabel={(option: any) => option?.value || ""}
                    isOptionEqualToValue={(option: any, value: any) => option.id === value}
                    value={filterList?.workExperience?.find((option: any) => option.id === field.value) || null}
                    onChange={(_, newValue: any) => field.onChange(newValue?.id ?? null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Experience Required"
                        margin="normal"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />

              <SectionHeader icon={PaymentsOutlined}>Salary &amp; Application</SectionHeader>

              <Box display="flex" flexDirection="row" gap={4}>
                <Controller
                  name="salaryRangeId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      fullWidth
                      disabled={viewMode}
                      options={filterList?.salaryRange || []}
                      getOptionLabel={(option: any) => option?.value || ""}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value}
                      value={filterList?.salaryRange?.find((option: any) => option.id === field.value) || null}
                      onChange={(_, newValue: any) => field.onChange(newValue?.id ?? null)}
                      renderInput={(params) => <TextField {...params} label="Salary Range (Optional)" margin="normal" />}
                    />
                  )}
                />
                <Controller
                  name="expiryDate"
                  control={control}
                  rules={{
                    required: "Apply-by date is required",
                    validate: (value) =>
                      !value || dayjs(value).isAfter(dayjs(), "day") || "Apply-by date must be in the future",
                  }}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      label="Apply By Date"
                      disablePast
                      readOnly={viewMode}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(value: Dayjs | null) => field.onChange(value ? value.format("YYYY-MM-DD") : null)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Controller
                name="noOfPosts"
                control={control}
                rules={{
                  min: { value: 1, message: "Must be at least 1" },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    fullWidth
                    type="number"
                    label="No. of Positions"
                    margin="normal"
                    disabled={viewMode}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    value={field.value ?? 1}
                    onChange={(e) => {
                      const value = e.target.value === "" ? null : parseInt(e.target.value, 10);
                      field.onChange(Number.isNaN(value) ? null : value);
                    }}
                    inputProps={{ min: 1 }}
                    sx={{ maxWidth: 240 }}
                  />
                )}
              />

              <Box display="flex" flexDirection="row" justifyContent="space-between" mt={5}>
                <Button
                  onClick={() => navigate(-1)}
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  sx={{ color: black }}
                  disabled={isSubmitting}
                >
                  {viewMode ? "Back" : "Cancel"}
                </Button>
                {!viewMode && (
                  <Button
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting || !requiredFieldsFilled}
                    variant="contained"
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : isEdit ? (
                        <SaveOutlined />
                      ) : (
                        <AddRounded />
                      )
                    }
                  >
                    {isSubmitting ? (isEdit ? "Saving..." : "Adding...") : isEdit ? "Save Changes" : "Add Job"}
                  </Button>
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
}

// Reused for the State field only - kept local since nothing else in this
// form needs its own query key for the state list.
function StateAutocomplete({
  value,
  onChange,
  disabled,
  error,
}: {
  value: number | null;
  onChange: (val: number | null) => void;
  disabled?: boolean;
  error?: string;
}) {
  const { data: stateList } = useQuery(["jobFormStateList"], async () => {
    const response = await getStateMasterData();
    return response?.data?.data?.list || [];
  });
  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      options={stateList || []}
      getOptionLabel={(option: any) => option?.value || ""}
      isOptionEqualToValue={(option: any, val: any) => option.id === val}
      value={(stateList || []).find((option: any) => option.id === value) || null}
      onChange={(_, newValue: any) => onChange(newValue?.id ?? null)}
      renderInput={(params) => (
        <TextField {...params} required label="State" margin="normal" error={!!error} helperText={error} />
      )}
    />
  );
}
